import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import RIcon from "./ReservasIcons";

// ---- Utilities ----
export const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=1400&q=80";

// Beauty (salons) falls back to a salon photo instead of the bar one above.
export const BEAUTY_FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=1400&q=80";

export const WEEK_DAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Mesas/Canchas subsection tabs, reused across the reservas (bars) and
// canchas (clubs) listing pages — mirrors the same Mesas/Canchas split used
// in Historial.jsx, but navigates between the two standalone listing routes
// instead of filtering a single combined list.
export function ReservationKindTabs({ active }) {
  const navigate = useNavigate();
  const tabs = [
    { key: "mesas", label: "Mesas", path: "/reservas" },
    { key: "canchas", label: "Canchas", path: "/canchas" },
    { key: "beauty", label: "Beauty", path: "/beauty" },
  ];
  return (
    <div className="hist-tabs" role="tablist" aria-label="Tipo de reserva">
      {tabs.map(({ key, label, path }) => (
        <button
          key={key}
          type="button"
          className={`hist-tab ${active === key ? "on" : ""}`}
          onClick={() => { if (active !== key) navigate(path); }}
          role="tab"
          aria-selected={active === key}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export const resolveUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (/^blob:/i.test(u)) return u;
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5600";
  if (u.startsWith("/")) return `${apiUrl}${u}`;
  return `${apiUrl}/${u}`;
};

export const resolveInstagramProfile = (value) => {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;

  const sanitizeHandle = (candidate) =>
    String(candidate || "")
      .trim()
      .replace(/^@+/, "")
      .replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, "")
      .replace(/^\/+/, "")
      .split(/[/?#]/)[0]
      .replace(/\/+$/, "");

  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      const handle = sanitizeHandle(parsed.pathname);
      if (!handle) return null;
      return {
        handle: `@${handle}`,
        href: `${parsed.protocol}//${parsed.host}/${handle}/`,
      };
    }
  } catch (_) {
    // Fall through to plain-handle normalization.
  }

  const handle = sanitizeHandle(raw);
  if (!handle) return null;

  return {
    handle: `@${handle}`,
    href: `https://instagram.com/${handle}`,
  };
};

export const extractBarPayload = (response) => response?.data?.info || {};

// ---- COUNTRIES config ----
export const COUNTRIES = [
  {
    code: "AR",
    prefix: "+54",
    label: "AR +54",
    length: 10,
    placeholder: "1123456789",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 10;
    },
    flag: (
      <svg viewBox="0 0 3 2" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="3" height="2" fill="#74acdf" />
        <rect y="0.66" width="3" height="0.68" fill="#ffffff" />
        <circle cx="1.5" cy="1" r="0.15" fill="#f6b40e" />
      </svg>
    )
  },
  {
    code: "BO",
    prefix: "+591",
    label: "BO +591",
    length: 8,
    placeholder: "71234567",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 8;
    },
    flag: (
      <svg viewBox="0 0 3 2" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="3" height="2" fill="#e31a1c" />
        <rect y="0.66" width="3" height="0.68" fill="#ffd700" />
        <rect y="1.34" width="3" height="0.66" fill="#007a33" />
      </svg>
    )
  },
  {
    code: "CL",
    prefix: "+56",
    label: "CL +56",
    length: 9,
    placeholder: "912345678",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 9;
    },
    flag: (
      <svg viewBox="0 0 3 2" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="3" height="2" fill="#d52b1e" />
        <rect width="3" height="1" fill="#ffffff" />
        <rect width="1" height="1" fill="#0039a6" />
        <polygon points="0.5,0.25 0.58,0.5 0.35,0.35 0.65,0.35 0.42,0.5" fill="#ffffff" transform="scale(0.8) translate(0.12, 0.05)" />
      </svg>
    )
  },
  {
    code: "CO",
    prefix: "+57",
    label: "CO +57",
    length: 10,
    placeholder: "3001234567",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 10;
    },
    flag: (
      <svg viewBox="0 0 3 2" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="3" height="2" fill="#ffd700" />
        <rect y="1" width="3" height="0.5" fill="#003893" />
        <rect y="1.5" width="3" height="0.5" fill="#ce1126" />
      </svg>
    )
  },
  {
    code: "UY",
    prefix: "+598",
    label: "UY +598",
    length: 9,
    placeholder: "94123456",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 8 || clean.length === 9;
    },
    flag: (
      <svg viewBox="0 0 9 6" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="9" height="6" fill="#ffffff" />
        <rect y="0.67" width="9" height="0.67" fill="#0038a8" />
        <rect y="2" width="9" height="0.67" fill="#0038a8" />
        <rect y="3.33" width="9" height="0.67" fill="#0038a8" />
        <rect y="4.67" width="9" height="0.67" fill="#0038a8" />
        <rect width="3.33" height="3.33" fill="#ffffff" />
        <circle cx="1.67" cy="1.67" r="0.6" fill="#ffd700" />
      </svg>
    )
  },
  {
    code: "PY",
    prefix: "+595",
    label: "PY +595",
    length: 9,
    placeholder: "981123456",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 9;
    },
    flag: (
      <svg viewBox="0 0 3 2" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="3" height="2" fill="#d52b1e" />
        <rect y="0.66" width="3" height="0.68" fill="#ffffff" />
        <rect y="1.34" width="3" height="0.66" fill="#003893" />
      </svg>
    )
  },
  {
    code: "PE",
    prefix: "+51",
    label: "PE +51",
    length: 9,
    placeholder: "912345678",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 9;
    },
    flag: (
      <svg viewBox="0 0 3 2" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="3" height="2" fill="#ffffff" />
        <rect width="1" height="2" fill="#d52b1e" />
        <rect x="2" width="1" height="2" fill="#d52b1e" />
      </svg>
    )
  },
  {
    code: "BR",
    prefix: "+55",
    label: "BR +55",
    length: 11,
    placeholder: "11912345678",
    validate: (val) => {
      const clean = val.replace(/\D/g, "");
      return clean.length === 10 || clean.length === 11;
    },
    flag: (
      <svg viewBox="0 0 10 7" width="20" height="14" style={{ borderRadius: "2px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)", display: "block" }}>
        <rect width="10" height="7" fill="#009739" />
        <polygon points="5,0.7 9.3,3.5 5,6.3 0.7,3.5" fill="#ffd100" />
        <circle cx="5" cy="3.5" r="1.8" fill="#002776" />
      </svg>
    )
  }
];

// ---- QtySelect ----
export const QtySelect = ({ value = '', options = [], onChange, disabled = false, className = '', placeholder = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => ({
      value: opt.value,
      label: opt.label ?? opt.value,
      disabled: Boolean(opt.disabled),
    }));
  }, [options]);
  const selectedOption = normalizedOptions.find((opt) => String(opt.value) === String(value)) || null;
  const hasValue = Boolean(selectedOption) && !selectedOption.disabled;
  const displayValue = hasValue
    ? String(selectedOption.label ?? selectedOption.value ?? '')
    : placeholder
      ? String(placeholder)
      : String(value ?? '');
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);
  const toggle = (e) => {
    if (disabled) return;
    if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.ui-options')) return;
    setOpen((o) => !o);
  };
  const onKey = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
    if (e.key === 'Escape') setOpen(false);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const enabledOptions = normalizedOptions.filter((opt) => !opt.disabled);
      if (!enabledOptions.length) return;
      const currentIdx = enabledOptions.findIndex((opt) => String(opt.value) === String(value));
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      let nextIdx;
      if (currentIdx === -1) {
        nextIdx = dir === 1 ? 0 : enabledOptions.length - 1;
      } else {
        nextIdx = Math.min(enabledOptions.length - 1, Math.max(0, currentIdx + dir));
      }
      const nextOpt = enabledOptions[nextIdx];
      if (nextOpt && onChange) onChange(nextOpt.value);
    }
  };
  const pick = (opt) => {
    if (!opt || opt.disabled) return;
    setOpen(false);
    if (onChange) onChange(opt.value);
  };
  const containerClass = ['ui-select', className, disabled ? 'is-disabled' : '', open ? 'is-open' : '', !hasValue && placeholder ? 'is-placeholder' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div
      ref={ref}
      className={containerClass}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={onKey}
      onClick={toggle}
      role="listbox"
      aria-expanded={open}
      aria-disabled={disabled}
    >
      <div className="ui-select-value">{displayValue}</div>
      <div className="ui-options" style={{ display: open ? 'block' : 'none' }}>
        {placeholder ? (
          <div
            key="placeholder"
            className="ui-option is-disabled"
            role="option"
            aria-selected={!hasValue}
            aria-disabled="true"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            {String(placeholder)}
          </div>
        ) : null}
        {normalizedOptions.map((opt) => {
          const isSelected = String(opt.value) === String(value);
          return (
            <div
              key={String(opt.value)}
              className={`ui-option ${isSelected ? 'is-selected' : ''} ${opt.disabled ? 'is-disabled' : ''}`}
              role="option"
              aria-selected={isSelected}
              aria-disabled={opt.disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt)}
            >
              {String(opt.label ?? opt.value)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- HoldCountdown ----
export function HoldCountdown({ expiresAt, onExpired, label = "Tu lugar está reservado por" }) {
  const [remaining, setRemaining] = useState(() => {
    const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
    return diff;
  });

  useEffect(() => {
    if (remaining <= 0) { onExpired && onExpired(); return; }
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { onExpired && onExpired(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt, onExpired]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const urgent = remaining < 60;

  return (
    <div className={`hold-countdown${urgent ? " urgent" : ""}`}>
      <RIcon name="clock" width={15} height={15} />
      {label}{" "}
      <strong>{mins}:{String(secs).padStart(2, "0")}</strong> min
    </div>
  );
}

export function formatPillDateLabel(date) {
  if (!date || !date.isValid()) return 'Elegir fecha';
  const today = dayjs().startOf('day');
  const tomorrow = dayjs().add(1, 'day').startOf('day');
  const target = date.clone().startOf('day');
  if (target.isSame(today, 'day')) {
    return `Hoy ${target.format('DD/MM')}`;
  }
  if (target.isSame(tomorrow, 'day')) {
    return `Mañana ${target.format('DD/MM')}`;
  }
  const dayNames = ['Dom.', 'Lun.', 'Mar.', 'Mié.', 'Jue.', 'Vie.', 'Sáb.'];
  const dayName = dayNames[target.day()];
  return `${dayName} ${target.format('DD/MM')}`;
}

// ---- SingleDatePickerPill ----
export function SingleDatePickerPill({ value, onChange, minDate, maxDate, ariaLabel, isDateDisabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const [tempDay, setTempDay] = useState(value);

  useEffect(() => {
    setTempDay(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (e.target.closest?.(".ant-picker-dropdown")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const applyAndClose = () => {
    if (tempDay && tempDay.isValid()) {
      onChange(tempDay);
    }
    setOpen(false);
  };

  // Optional extra rule (e.g. closed weekdays) on top of the min/max range.
  const blocked = (day) => Boolean(isDateDisabled && isDateDisabled(day));

  const setQuickToday = () => {
    const today = dayjs().startOf("day");
    setTempDay(today);
  };

  const setQuickTomorrow = () => {
    const tomorrow = dayjs().add(1, 'day').startOf("day");
    setTempDay(tomorrow);
  };

  const label = formatPillDateLabel(value);

  const menuInner = (
    <div
      ref={menuRef}
      className="pill-menu pill-menu-date pill-menu-single-date"
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      style={{
        background: '#ffffff',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        boxShadow: '0 16px 40px rgba(0,0,0,0.16)',
        padding: '12px'
      }}
    >
      <div>
        <div style={{ fontWeight: 700, marginBottom: 8, color: '#2D3035', fontSize: '13px' }}>Elegir fecha</div>
        <div className="pill-quick-row" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button type="button" className="pill-quick" disabled={blocked(dayjs().startOf("day"))} onClick={setQuickToday}>Hoy</button>
          <button type="button" className="pill-quick" disabled={blocked(dayjs().add(1, 'day').startOf("day"))} onClick={setQuickTomorrow}>Mañana</button>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          <div ref={calendarContainerRef} style={{ position: 'relative', minHeight: '280px' }} />
          <DatePicker
            value={tempDay}
            onChange={(d) => d && setTempDay(d.startOf("day"))}
            disabledDate={(current) => {
              return current && (current.isBefore(minDate, "day") || current.isAfter(maxDate, "day") || blocked(current));
            }}
            style={{ width: '100%' }}
            getPopupContainer={() => calendarContainerRef.current || document.body}
            open={true}
            allowClear={false}
            dropdownClassName="pill-inline-range"
          />
          <div className="pill-menu-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <button type="button" className="pill-action secondary" onClick={() => setOpen(false)}>Cerrar</button>
            <div style={{ flex: 1 }} />
            <button type="button" className="pill-action primary" onClick={applyAndClose}>Aplicar</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={ref} className={`pill-select ${open ? 'open' : ''}`} style={{ width: '100%', position: 'relative' }}>
      <RIcon name="cal" className="icon" width={17} height={17} style={{ position: 'absolute', left: '12px', color: 'var(--zoco-gray-2)' }} />
      <button type="button" className="pill-trigger" aria-label={ariaLabel} onClick={() => setOpen(v => !v)}>
        {label}
      </button>
      <CherowIcon className="arrow" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      {open && (
        <>
          <div className="pill-backdrop" onClick={() => setOpen(false)} />
          {menuInner}
        </>
      )}
    </div>
  );
}

/* ---- Custom Chevron Arrow Icon ---- */
export const CherowIcon = ({ className, style }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none" style={{ flexShrink: 0, pointerEvents: 'none', ...style }}>
    <path d="M1 1L7 7L13 1" stroke="#2D3035" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ---- PillDropdown: Province & City dropdowns ---- */
export const PillDropdown = ({ widthClass = "", iconName, value, options = [], onChange, placeholder = "Seleccionar", searchable = false, ariaLabel, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current) return;
      const clickedInsideTrigger = ref.current.contains(e.target);
      const clickedInsideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!clickedInsideTrigger && !clickedInsideMenu) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const recalcMenuPos = () => {
    try {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const width = Math.min(320, vw - 28);
      const idealLeft = r.left + r.width / 2 - width / 2;
      const left = Math.max(14, Math.min(vw - width - 14, idealLeft));
      const top = r.bottom + 10;
      setMenuPos({ left, top, width });
    } catch (_) { }
  };

  useEffect(() => {
    if (open && (typeof window !== 'undefined') && window.innerWidth <= 430) {
      recalcMenuPos();
      const onScroll = () => recalcMenuPos();
      const onResize = () => recalcMenuPos();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
      };
    }
  }, [open]);

  const current = options.find((o) => String(o.value) === String(value));
  const label = current ? current.label : (placeholder || options[0]?.label || "Seleccionar");
  const filtered = !searchable || !query
    ? options
    : options.filter(o => String(o.label).toLowerCase().includes(query.toLowerCase()));

  const isMobile = (typeof window !== 'undefined' && window.innerWidth <= 430);

  const menuInner = (
    <div ref={menuRef} className="pill-menu" role="listbox" onClick={(e) => e.stopPropagation()} style={isMobile ? { position: 'static', top: 'auto', left: 'auto', right: 'auto', width: '100%' } : undefined}>
      {searchable && (
        <div className="pill-menu-search">
          <input type="text" placeholder="Buscar..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}
      <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
        {filtered.map((opt) => (
          <button
            type="button"
            key={String(opt.value)}
            className={`pill-menu-item ${String(opt.value) === String(value) ? 'active' : ''}`}
            onClick={() => { setOpen(false); onChange && onChange(opt.value, opt); setQuery(""); }}
            role="option"
            aria-selected={String(opt.value) === String(value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div ref={ref} className={`pill-select ${widthClass} ${open ? 'open' : ''}`} style={{ position: 'relative' }}>
      {iconName && <RIcon name={iconName} className="icon" width={17} height={17} />}
      <button type="button" className="pill-trigger" aria-label={ariaLabel} disabled={disabled} onClick={() => setOpen(v => !v)} style={disabled ? { cursor: 'not-allowed', opacity: 0.55 } : undefined}>{label}</button>
      <CherowIcon className="arrow" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      {open && !disabled && (
        isMobile
          ? createPortal(
            <div style={{ position: 'fixed', left: menuPos?.left ?? 16, top: menuPos?.top ?? 80, width: menuPos?.width ?? Math.min(320, window.innerWidth - 28), right: 'auto', zIndex: 10000 }}>
              {menuInner}
            </div>,
            document.body
          )
          : menuInner
      )}
    </div>
  );
};

/* ---- MultiPillDropdown: Multi-select pill dropdowns ---- */
export const MultiPillDropdown = ({ widthClass = "", iconName, value = [], options = [], onChange, placeholder = "Seleccionar", searchable = false, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current) return;
      const clickedInsideTrigger = ref.current.contains(e.target);
      const clickedInsideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!clickedInsideTrigger && !clickedInsideMenu) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const recalcMenuPos = () => {
    try {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const width = Math.min(320, vw - 28);
      const idealLeft = r.left + r.width / 2 - width / 2;
      const left = Math.max(14, Math.min(vw - width - 14, idealLeft));
      const top = r.bottom + 10;
      setMenuPos({ left, top, width });
    } catch (_) { }
  };

  useEffect(() => {
    if (open && (typeof window !== 'undefined') && window.innerWidth <= 430) {
      recalcMenuPos();
      const onScroll = () => recalcMenuPos();
      const onResize = () => recalcMenuPos();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
      };
    }
  }, [open]);

  const handleToggleOption = (val) => {
    let newValue;
    const stringVal = String(val);
    const hasVal = value.map(String).includes(stringVal);
    
    if (hasVal) {
      newValue = value.filter((v) => String(v) !== stringVal);
    } else {
      newValue = [...value, val];
    }
    if (onChange) onChange(newValue);
  };

  const label = useMemo(() => {
    if (!value || value.length === 0) return placeholder;
    const selectedLabels = options
      .filter((opt) => value.map(String).includes(String(opt.value)))
      .map((opt) => opt.label || opt.value);
    
    if (selectedLabels.length === 0) return placeholder;
    if (selectedLabels.length <= 2) return selectedLabels.join(", ");
    return `${selectedLabels.length} seleccionados`;
  }, [value, options, placeholder]);

  const filtered = !searchable || !query
    ? options
    : options.filter(o => String(o.label).toLowerCase().includes(query.toLowerCase()));

  const isMobile = (typeof window !== 'undefined' && window.innerWidth <= 430);

  const menuInner = (
    <div ref={menuRef} className="pill-menu" role="listbox" onClick={(e) => e.stopPropagation()} style={isMobile ? { position: 'static', top: 'auto', left: 'auto', right: 'auto', width: '100%' } : undefined}>
      {searchable && (
        <div className="pill-menu-search">
          <input type="text" placeholder="Buscar..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}
      <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
        {filtered.map((opt) => {
          const isSelected = value.map(String).includes(String(opt.value));
          return (
            <button
              type="button"
              key={String(opt.value)}
              className={`pill-menu-item ${isSelected ? 'active' : ''}`}
              onClick={() => handleToggleOption(opt.value)}
              role="option"
              aria-selected={isSelected}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px' }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                readOnly
                style={{ cursor: 'pointer', margin: 0, width: '16px', height: '16px', flexShrink: 0, accentColor: 'var(--zr-lime, var(--zoco-lime))' }}
              />
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={ref} className={`pill-select ${widthClass} ${open ? 'open' : ''}`} style={{ position: 'relative' }}>
      {iconName && <RIcon name={iconName} className="icon" width={17} height={17} />}
      <button type="button" className="pill-trigger" aria-label={ariaLabel} onClick={() => setOpen(v => !v)}>{label}</button>
      <CherowIcon className="arrow" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      {open && (
        isMobile
          ? createPortal(
            <div style={{ position: 'fixed', left: menuPos?.left ?? 16, top: menuPos?.top ?? 80, width: menuPos?.width ?? Math.min(320, window.innerWidth - 28), right: 'auto', zIndex: 10000 }}>
              {menuInner}
            </div>,
            document.body
          )
          : menuInner
      )}
    </div>
  );
};

